'use client'

import { useFormatter, useTranslations } from 'next-intl'
import { useState } from 'react'

import { getGatewayErrorCode } from '@/api/client'
import { useCreateWithdrawal } from '@/features/clubs/hooks/useClubMonetization'
import {
  type ClubFinance,
  microToUsdt,
  usdtFor,
  withdrawalAmountError
} from '@/features/clubs/lib/clubMonetization'
import { formatLeaderboardNumber } from '@/features/leaderboard/formatLeaderboardNumber'
import Dialog, { DialogDescription } from '@/ui/Dialog'
import { StepCheckIcon } from '@/ui/icons/StepCheckIcon'

import S from './ClubMonetization.module.scss'

const SERVER_ERRORS: Record<string, string> = {
  below_min: 'errors.belowMin',
  insufficient_balance: 'errors.aboveBalance',
  monetization_unavailable: 'locked'
}

interface WithdrawDialogProps {
  open: boolean
  onClose: () => void
  finance: ClubFinance
}

/**
 * «Withdrawal request» (design): the amount in AP, its approximate USDT at
 * the admin-set rate and a disclaimer; the request goes to Arena managers.
 */
const WithdrawDialog = ({ open, onClose, finance }: WithdrawDialogProps) => {
  const t = useTranslations('club.monetization')
  const format = useFormatter()
  const [digits, setDigits] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const create = useCreateWithdrawal()

  const amount = Number(digits)
  const amountError = digits ? withdrawalAmountError(amount, finance) : null
  const canSend = digits !== '' && amountError === null && !create.isPending
  const usdt = (value: number) =>
    format.number(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const rate = format.number(microToUsdt(finance.rate_micro_usdt), {
    maximumFractionDigits: 6
  })
  // No rate set by the admins yet: no USDT anywhere, no disclaimer.
  const hasRate = finance.rate_micro_usdt > 0
  const serverCode = create.isError ? getGatewayErrorCode(create.error) : null
  const errorKey =
    amountError === 'belowMin' || amountError === 'aboveBalance'
      ? `errors.${amountError}`
      : create.isError
      ? (serverCode && SERVER_ERRORS[serverCode]) || 'errors.generic'
      : null

  const close = () => {
    if (create.isPending) {
      return
    }
    setDigits('')
    setSubmitted(false)
    create.reset()
    onClose()
  }

  const send = () => {
    create.mutate(amount, { onSuccess: () => setSubmitted(true) })
  }

  if (submitted) {
    return (
      <Dialog
        open={open}
        toggle={value => !value && close()}
        className={S.dialog}
        describedByChildren
      >
        <div className={S.success}>
          <span className={S.successIcon} aria-hidden>
            <StepCheckIcon />
          </span>
          <h2 className={S.dialogTitle}>{t('success.title')}</h2>
          <DialogDescription className={S.successText}>
            {t('success.text')}
          </DialogDescription>
          <button type="button" className={S.secondaryButton} onClick={close}>
            {t('success.gotIt')}
          </button>
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog
      open={open}
      toggle={value => !value && close()}
      title={t('request')}
      description={hasRate ? t('disclaimer') : t('request')}
      className={S.dialog}
    >
      <div className={S.form}>
        <div className={S.available}>
          <span className={S.availableLabel}>{t('available')}</span>
          <span className={S.availableValue}>
            {format.number(finance.balance_ap)} AP{' '}
            {hasRate ? (
              <span className={S.availableUsdt}>
                ≈ {usdt(usdtFor(finance.balance_ap, finance.rate_micro_usdt))}{' '}
                USDT*
              </span>
            ) : null}
          </span>
        </div>

        <label className={S.amountLabel}>
          <span className={S.amountLabelRow}>
            <span>{t('amount')}</span>
            {finance.min_withdrawal_ap > 0 ? (
              <span className={S.hint}>
                {t('min', {
                  amount: formatLeaderboardNumber(finance.min_withdrawal_ap)
                })}
              </span>
            ) : null}
          </span>
          <span className={S.amountField}>
            <input
              className={S.amountInput}
              inputMode="numeric"
              placeholder={t('placeholder')}
              value={digits ? format.number(amount) : ''}
              onChange={event => {
                create.reset()
                setDigits(event.target.value.replaceAll(/\D/g, '').slice(0, 15))
              }}
              aria-invalid={Boolean(errorKey) || undefined}
            />
            <button
              type="button"
              className={S.maxButton}
              onClick={() => setDigits(String(finance.balance_ap))}
              disabled={finance.balance_ap <= 0}
            >
              {t('max')}
            </button>
          </span>
          {errorKey ? (
            <span className={S.fieldError} role="alert">
              {t(errorKey, {
                amount: formatLeaderboardNumber(finance.min_withdrawal_ap)
              })}
            </span>
          ) : null}
        </label>

        {hasRate ? (
          <dl className={S.estimate}>
            <div className={S.estimateRow}>
              <dt>{t('rate')}</dt>
              <dd>1 AP ≈ {rate} USDT</dd>
            </div>
            <div className={S.estimateRow}>
              <dt>{t('receive')}</dt>
              <dd>
                {amountError === null && digits
                  ? `≈ ${usdt(usdtFor(amount, finance.rate_micro_usdt))}`
                  : '0'}{' '}
                USDT
              </dd>
            </div>
          </dl>
        ) : null}

        <button
          type="button"
          className={S.primaryButtonLarge}
          onClick={send}
          disabled={!canSend}
        >
          {t('send')}
        </button>
        {hasRate ? <p className={S.disclaimer}>{t('disclaimer')}</p> : null}
      </div>
    </Dialog>
  )
}

export default WithdrawDialog
